/**
 * A component that renders the input fields for creating or editing an accepted
 * clarification post.
 *
 * It contains 2 sections:
 * - a text input field for the content of the post
 * - 4 input fields for the minutes and second parts of the starting and ending
 *   timestamps for the clarification. These timestamps dictate when the clarification
 *   will appear on top of the video as it plays.
 */
import {css, StyleSheet} from "aphrodite";
import * as React from "react";
import {connect} from "react-redux";

import {View} from "@khanacademy/wonder-blocks-core";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import type {ApolloGqlClient} from "@khan/data-gql-apollo";
import {doNotTranslate} from "@khan/i18n";

import type {OfficialClarification} from "#clarifications-package/types.ts";
import {
    getTimestampFromSeconds,
    getTimestampSecondsFromText,
} from "#components/formatted-markdown-package/format-content.tsx";
import type {WithApolloProps} from "#data-access/gql/with-apollo.tsx";

import * as sharedStyles from "../shared-styles.ts";
import type {Post, PostKey} from "../types.ts";

import {
    acceptClarificationAction,
    editAcceptedClarificationAction,
} from "./discussion-actions.ts";
import DiscussionInput from "./discussion-input.tsx";
import type {State as ReducerState} from "./discussion-reducer.ts";

type OwnProps = {
    post: Post | OfficialClarification;
    editingOrAccepting: "editing" | "accepting";
    topicSlug: string;
    onCancel: () => void;
};

type ReduxProps = {
    contentId: string;
    onSubmitClarificationAcceptance: (
        postKey: PostKey,
        avatarSrc: string,
        nickname: string,
        kaid: string,
        content: string,
        openTimestamp: number,
        closeTimestamp: number,
        gqlClient: ApolloGqlClient,
    ) => void;
    onSubmitClarificationEdit: (
        postContent: string,
        openTimestamp: number,
        closeTimestamp: number,
    ) => void;
};

type Props = OwnProps & ReduxProps & WithApolloProps;

type State = {
    startingTimestampMinutes: number;
    startingTimestampSeconds: number;
    endingTimestampMinutes: number;
    endingTimestampSeconds: number;
};

export class AcceptedClarificationInput extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = this.getStartAndEndFromProps();
    }

    componentDidUpdate(prevProps: Readonly<any>): void {
        if (
            this.props.editingOrAccepting !== prevProps.editingOrAccepting ||
            // @ts-expect-error - TS2339 - Property 'openTimestamp' does not exist on type 'OfficialClarification | Post'.
            this.props.post.openTimestamp !== prevProps.post.openTimestamp ||
            // @ts-expect-error - TS2339 - Property 'closeTimestamp' does not exist on type 'OfficialClarification | Post'.
            this.props.post.closeTimestamp !== prevProps.post.closeTimestamp ||
            // @ts-expect-error - TS2339 - Property 'content' does not exist on type 'OfficialClarification | Post'.
            this.props.post.content !== prevProps.post.content
        ) {
            /**
             * As per the React documentation, it is perfectly fine to call
             * setState from componentDidUpdate as long as it is guarded
             * with a condition that does not rely on the state being set.
             *
             * https://reactjs.org/docs/react-component.html#static-getderivedstatefromprops
             * > If you need to perform a side effect (for example, data
             * > fetching or an animation) in response to a change in props,
             * > use componentDidUpdate lifecycle instead.
             *
             * https://reactjs.org/docs/react-component.html#componentdidupdate
             * > You may call setState() immediately in componentDidUpdate()
             * > but note that it must be wrapped in a condition, or you’ll
             * > cause an infinite loop.
             */

            this.setState(this.getStartAndEndFromProps());
        }
    }

    getStartAndEndFromProps: () => State = (): State => {
        const {post, editingOrAccepting} = this.props;

        let startingTimeTotalSeconds = 20;
        let endingTimeTotalSeconds = 40;

        if (
            editingOrAccepting === "editing" &&
            // This check just makes sure that the type of post we're editing
            // matches an OfficialClarification.
            // @ts-expect-error - TS2339 - Property 'closeTimestamp' does not exist on type 'OfficialClarification | Post'.
            post.closeTimestamp
        ) {
            // @ts-expect-error - TS2339 - Property 'openTimestamp' does not exist on type 'OfficialClarification | Post'.
            startingTimeTotalSeconds = post.openTimestamp;
            // @ts-expect-error - TS2339 - Property 'closeTimestamp' does not exist on type 'OfficialClarification | Post'.
            endingTimeTotalSeconds = post.closeTimestamp;
            // @ts-expect-error - TS2339 - Property 'content' does not exist on type 'OfficialClarification | Post'.
        } else if (post.content) {
            // If editingOrAccepting is not editing, the post should always have a content prop,
            // but TypeScript doesn't believe us, so this is an else if clause instead of an else.
            startingTimeTotalSeconds = getTimestampSecondsFromText(
                // @ts-expect-error - TS2339 - Property 'content' does not exist on type 'OfficialClarification | Post'.
                post.content,
            );
            endingTimeTotalSeconds = startingTimeTotalSeconds + 20;
        }

        const startingTimeTimestampObj = getTimestampFromSeconds(
            startingTimeTotalSeconds,
        );
        const endingTimeTimestampObj = getTimestampFromSeconds(
            endingTimeTotalSeconds,
        );

        return {
            startingTimestampMinutes: Number(startingTimeTimestampObj.minutes),
            startingTimestampSeconds: Number(startingTimeTimestampObj.seconds),
            endingTimestampMinutes: Number(endingTimeTimestampObj.minutes),
            endingTimestampSeconds: Number(endingTimeTimestampObj.seconds),
        };
    };

    handleSubmitAcceptedClarification: (postContent: string) => void = (
        postContent: string,
    ) => {
        const {
            post,
            editingOrAccepting,
            onSubmitClarificationAcceptance,
            onSubmitClarificationEdit,
            gqlClient,
        } = this.props;
        const {
            startingTimestampMinutes,
            startingTimestampSeconds,
            endingTimestampMinutes,
            endingTimestampSeconds,
        } = this.state;

        const startingTimeTotalSeconds =
            startingTimestampMinutes * 60 + startingTimestampSeconds;
        const endingTimeTotalSeconds =
            endingTimestampMinutes * 60 + endingTimestampSeconds;

        if (startingTimeTotalSeconds >= endingTimeTotalSeconds) {
            // TODO(diedra): Show input error stating that the ending timestamp needs
            // to be later in time than the starting timestamp.
            return;
        }

        if (editingOrAccepting === "editing") {
            onSubmitClarificationEdit(
                postContent,
                startingTimeTotalSeconds,
                endingTimeTotalSeconds,
            );
            // @ts-expect-error - TS2339 - Property 'authorAvatarSrc' does not exist on type 'OfficialClarification | Post'.
        } else if (post.authorAvatarSrc) {
            onSubmitClarificationAcceptance(
                // @ts-expect-error - TS2339 - Property 'key' does not exist on type 'OfficialClarification | Post'.
                post.key,
                // @ts-expect-error - TS2339 - Property 'authorAvatarSrc' does not exist on type 'OfficialClarification | Post'.
                post.authorAvatarSrc,
                // @ts-expect-error - TS2339 - Property 'authorNickname' does not exist on type 'OfficialClarification | Post'.
                post.authorNickname,
                // @ts-expect-error - TS2339 - Property 'authorKaid' does not exist on type 'OfficialClarification | Post'.
                post.authorKaid,
                postContent,
                startingTimeTotalSeconds,
                endingTimeTotalSeconds,
                gqlClient,
            );
        }
    };

    renderClarificationTimestampInput: (
        timestampType: string,
    ) => React.ReactNode = (timestampType: string) => {
        const timestamp = this.state[timestampType];

        return (
            <input
                className={css(styles.acceptClarificationInput)}
                value={timestamp}
                onChange={(event) => {
                    const value = event.target.value;
                    const parsedValue = value.length ? Number(value) : 0;
                    // @ts-expect-error - TS2345 - Argument of type '{ [x: string]: number; }' is not assignable to parameter of type 'State | ((prevState: Readonly<State>, props: Readonly<Props>) => State | Pick<State, keyof State> | null) | Pick<...> | null'.
                    this.setState({[timestampType]: parsedValue});
                }}
                type="number"
                min={0}
                max={59}
            />
        );
    };

    render(): React.ReactNode {
        const {post, contentId, topicSlug, onCancel} = this.props;

        let feedbackKey = "";
        let initialPostContent = "";

        // @ts-expect-error - TS2339 - Property 'key' does not exist on type 'OfficialClarification | Post'.
        if (post.key) {
            // post is of type Post
            // @ts-expect-error - TS2339 - Property 'key' does not exist on type 'OfficialClarification | Post'.
            feedbackKey = post.key;
            // @ts-expect-error - TS2339 - Property 'content' does not exist on type 'OfficialClarification | Post'.
            initialPostContent = post.content;
            // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'OfficialClarification | Post'.
        } else if (post.id) {
            // post is of type OfficialClarification
            // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'OfficialClarification | Post'.
            feedbackKey = post.id;
            // @ts-expect-error - TS2339 - Property 'text' does not exist on type 'OfficialClarification | Post'.
            initialPostContent = post.text;
        }

        return (
            <View style={styles.replyInputContainer}>
                {/* @ts-expect-error - TS2741 - Property 'onPostSuccess' is missing in type '{ acceptingClarification: true; tab: "clarifications"; contentKind: string; contentId: string; topicSlug: string; feedbackKey: string; initialPostContent: string; onCancel: () => void; onSubmitAcceptedClarification: (postContent: string) => void; }' but required in type 'ExportProps'. */}
                <DiscussionInput
                    acceptingClarification={true}
                    tab="clarifications"
                    contentKind="Video"
                    contentId={contentId}
                    topicSlug={topicSlug}
                    feedbackKey={feedbackKey}
                    initialPostContent={initialPostContent}
                    onCancel={onCancel}
                    onSubmitAcceptedClarification={
                        this.handleSubmitAcceptedClarification
                    }
                />
                <View style={styles.acceptClarificationContainer}>
                    {doNotTranslate`From`}
                    {this.renderClarificationTimestampInput(
                        "startingTimestampMinutes",
                    )}
                    :
                    {this.renderClarificationTimestampInput(
                        "startingTimestampSeconds",
                    )}
                    {doNotTranslate`to`}
                    {this.renderClarificationTimestampInput(
                        "endingTimestampMinutes",
                    )}
                    :
                    {this.renderClarificationTimestampInput(
                        "endingTimestampSeconds",
                    )}
                </View>
            </View>
        );
    }
}

const styles = StyleSheet.create({
    // eslint-disable-next-line react-native/no-unused-styles
    spinner: {
        marginRight: spacing.xSmall_8,
    },
    replyInputContainer: {
        paddingBottom: spacing.large_24,
    },
    acceptClarificationContainer: {
        display: "block",
        marginLeft:
            sharedStyles.avatarContainerDiameter + sharedStyles.avatarFormSpace,
    },
    acceptClarificationInput: {
        width: 40,
        padding: 0,
        margin: "16px 4px 0 4px",
        textAlign: "right",
    },
});

const mapDispatchToProps = {
    onSubmitClarificationAcceptance: acceptClarificationAction,
    onSubmitClarificationEdit: editAcceptedClarificationAction,
} as const;

const mapStateToProps = (state: ReducerState) => {
    return {
        contentId: state.contentId,
    };
};

type ExportProps = Without<
    JSX.LibraryManagedAttributes<
        typeof AcceptedClarificationInput,
        React.ComponentProps<typeof AcceptedClarificationInput>
    >,
    ReduxProps
>;

const ConnectedAcceptedClarificationInput: React.ComponentType<ExportProps> =
    // @ts-expect-error - TS2345 - Argument of type 'typeof AcceptedClarificationInput' is not assignable to parameter of type 'ComponentType<Matching<{ contentId: string; } & { readonly onSubmitClarificationAcceptance: (key: string, avatarSrc: string, nickname: string, kaid: string, content: string, openTimestamp: number, closeTimestamp: number, gqlClient: ApolloGqlClient) => void; readonly onSubmitClarificationEdit: (postContent: string, o...'.
    connect(mapStateToProps, mapDispatchToProps)(AcceptedClarificationInput);

export default ConnectedAcceptedClarificationInput;
